import { useCallback, useEffect, useMemo, useState } from "react";
import { Box } from "@mui/material";
import { PageHeader } from "../../../components/ui";
import dayjs from "dayjs";
import {
  ClioService,
  NegotiationsReportItem,
  NegotiationsReportRequest,
  OpenAPI,
} from "../../../api";
import {
  fetchClioCustomFieldOptionNames,
  fetchClioUserNames,
} from "../../../components/shared/ClioSelects";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { NegotiationsFilters } from "./NegotiationsFilters";
import { NegotiationsTable } from "./NegotiationsTable";
import { mergeUniqueOptions, applySearchFilter, resetFilters, uniqueFilterOptions } from "./filtering";
import { computeCaseGroupSpans, groupRowsByCase, sortReportRows } from "./sorting";
import {
  DEFAULT_SORT_RULES,
  EMPTY_FILTERS,
  OVERDUE_FILTER_OPTIONS,
  type FilterFieldKey,
  type NegotiationsFiltersState,
  type SortRule,
} from "./types";

const MySwal = withReactContent(Swal);

const REPORT_DERIVED_FIELDS: FilterFieldKey[] = ["raa", "taskName", "taskAssignee"];

type PreloadedFilterOptions = Partial<Record<FilterFieldKey, string[]>>;

function formatDateParam(value: NegotiationsFiltersState["dueDateFrom"]): string | null {
  return value ? dayjs(value).format("YYYY-MM-DD") : null;
}

function toReportRequest(filters: NegotiationsFiltersState): NegotiationsReportRequest {
  return {
    searchTerm: filters.searchTerm.trim() || null,
    caseName: filters.caseName || null,
    raa: filters.raa || null,
    taskName: filters.taskName || null,
    taskAssignee: filters.taskAssignee || null,
    overdueOnly:
      filters.overdue === "Yes" ? true : filters.overdue === "No" ? false : null,
    dueDateFrom: formatDateParam(filters.dueDateFrom),
    dueDateTo: formatDateParam(filters.dueDateTo),
    taskedFrom: formatDateParam(filters.taskedFrom),
    taskedTo: formatDateParam(filters.taskedTo),
    negotiationStartedFrom: formatDateParam(filters.negotiationStartedFrom),
    negotiationStartedTo: formatDateParam(filters.negotiationStartedTo),
  };
}

/** Server fetch ignores search — search is applied client-side for instant typing. */
function toServerRequest(filters: NegotiationsFiltersState): NegotiationsReportRequest {
  return { ...toReportRequest(filters), searchTerm: null };
}

function triggerBlobDownload(blob: Blob, fileName: string) {
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.style.display = "none";
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(anchor);
}

export default function NegotiationsReport() {
  const [reportData, setReportData] = useState<NegotiationsReportItem[]>([]);
  const [preloadedOptions, setPreloadedOptions] = useState<PreloadedFilterOptions>({});
  const [filters, setFilters] = useState<NegotiationsFiltersState>(EMPTY_FILTERS);
  const [sortRules, setSortRules] = useState<SortRule[]>([...DEFAULT_SORT_RULES]);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    let cancelled = false;

    const loadFilterOptions = async () => {
      const [raa, taskAssignee] = await Promise.all([
        fetchClioCustomFieldOptionNames("raa"),
        fetchClioUserNames(true),
      ]);

      if (cancelled) return;

      setPreloadedOptions({ raa, taskAssignee });
    };

    void loadFilterOptions();
    return () => {
      cancelled = true;
    };
  }, []);

  const fetchReport = useCallback((nextFilters: NegotiationsFiltersState) => {
    setLoading(true);
    ClioService.postApiClioNegotiationsReport(toServerRequest(nextFilters))
      .then((data) => {
        setReportData(data);
        setHasLoaded(true);
        setPage(0);
      })
      .catch((error: { body?: { message?: string }; message?: string }) => {
        const detail =
          error?.body?.message?.trim() ||
          error?.message?.trim() ||
          "Unknown error";
        MySwal.fire({
          icon: "error",
          title: <p>Error loading negotiations report.</p>,
          text: detail,
        });
      })
      .finally(() => setLoading(false));
  }, []);

  const loadReport = useCallback(() => {
    fetchReport(filters);
  }, [fetchReport, filters]);

  const applyFilters = useCallback(
    (nextFilters: NegotiationsFiltersState, nextSortRules: SortRule[]) => {
      setSortRules(nextSortRules.length > 0 ? nextSortRules : [...DEFAULT_SORT_RULES]);
      setPage(0);

      const filtersChanged =
        JSON.stringify(toServerRequest(filters)) !==
        JSON.stringify(toServerRequest(nextFilters));
      setFilters(nextFilters);

      if (filtersChanged) {
        fetchReport(nextFilters);
      }
    },
    [fetchReport, filters]
  );

  const handleSearchChange = useCallback((searchTerm: string) => {
    setFilters((prev) => ({ ...prev, searchTerm }));
    setPage(0);
  }, []);

  const clearFilters = useCallback(() => {
    const empty = resetFilters();
    setFilters(empty);
    setPage(0);
    if (hasLoaded) {
      fetchReport(empty);
    }
  }, [fetchReport, hasLoaded]);

  const filterOptions = useMemo(() => {
    const fromReport = {} as Record<FilterFieldKey, string[]>;
    REPORT_DERIVED_FIELDS.forEach((field) => {
      fromReport[field] = uniqueFilterOptions(reportData, field);
    });

    return {
      searchTerm: [],
      caseName: uniqueFilterOptions(reportData, "caseName"),
      raa: mergeUniqueOptions(preloadedOptions.raa, fromReport.raa),
      taskName: fromReport.taskName ?? [],
      taskAssignee: mergeUniqueOptions(preloadedOptions.taskAssignee, fromReport.taskAssignee),
      overdue: [...OVERDUE_FILTER_OPTIONS],
    } as Record<FilterFieldKey, string[]>;
  }, [reportData, preloadedOptions]);

  const sortedData = useMemo(
    () => sortReportRows(applySearchFilter(reportData, filters.searchTerm), sortRules),
    [reportData, filters.searchTerm, sortRules]
  );

  const caseGroups = useMemo(() => groupRowsByCase(sortedData), [sortedData]);

  const paginatedRows = useMemo(() => {
    const pageGroups = caseGroups.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    return computeCaseGroupSpans(pageGroups.flat());
  }, [caseGroups, page, rowsPerPage]);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const response = await fetch(`${OpenAPI.BASE}/api/Clio/negotiationsReport/download`, {
        method: "POST",
        headers: {
          Authorization: (OpenAPI.HEADERS as Record<string, string>)?.Authorization,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(toReportRequest(filters)),
      });

      if (!response.ok) {
        throw new Error("Download failed");
      }

      triggerBlobDownload(
        await response.blob(),
        `NegotiationsReport-${dayjs().format("YYYY-MM-DD")}.xlsx`
      );
    } catch {
      MySwal.fire({
        icon: "error",
        title: <p>Error downloading negotiations report.</p>,
      });
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Box
      sx={{
        height: "var(--app-content-height)",
        display: "flex",
        flexDirection: "column",
        gap: 1,
        overflow: "hidden",
      }}
    >
      <PageHeader
        title="Negotiations"
        subtitle="Cases with negotiations."
      />

      <NegotiationsFilters
        filters={filters}
        filterOptions={filterOptions}
        sortRules={sortRules}
        loading={loading}
        downloading={downloading}
        canDownload={hasLoaded && reportData.length > 0}
        onLoad={loadReport}
        onApply={applyFilters}
        onSearchChange={handleSearchChange}
        onDownload={handleDownload}
        onClear={clearFilters}
      />

      <NegotiationsTable
        loading={loading}
        hasLoadedData={hasLoaded}
        uniqueCaseCount={caseGroups.length}
        taskCount={reportData.filter((r) => (r.taskId ?? 0) > 0).length}
        rows={paginatedRows}
        caseGroupCount={caseGroups.length}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={setPage}
        onRowsPerPageChange={(next) => {
          setRowsPerPage(next);
          setPage(0);
        }}
      />
    </Box>
  );
}
